import React, { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Icons,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';
import { createStyles } from './ScheduledPostsSummaryView.styles';
import { hashHistory } from 'react-router';
import formatDate from '@core/utils/formatDate';
import SchedulePostDateTimePickerDialog from '../../../Shared/ScheduleDateTimePickerDialog';

export interface ScheduledPostsSummaryViewProps {
  actionsDisabled?: boolean;
  actionsLabel: string;
  attachmentCount: number;
  attachmentSummary?: string;
  manageLabel?: string;
  onChangeScheduledPost: (date: Date) => void;
  onOpenActions?: (event: any) => void;
  onPublishNow: () => void;
  onView: () => void;
  preview: string;
  scheduledDate: string | null;
  summaryLabel: string;
  title: string;
  unavailableActionsLabel: string;
  viewLabel: string;
  renderPostTags: React.ReactNode;
}

export const ScheduledPostsSummaryView = ({
  actionsDisabled = false,
  actionsLabel,
  attachmentCount,
  attachmentSummary,
  manageLabel = 'Manage',
  onChangeScheduledPost,
  onOpenActions,
  onPublishNow,
  onView,
  preview,
  scheduledDate: currentScheduledDate,
  summaryLabel,
  title,
  unavailableActionsLabel,
  viewLabel,
  renderPostTags = <React.Fragment />,
}: ScheduledPostsSummaryViewProps) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const [openManageMenu, setOpenManageMenu] =
    useState<HTMLButtonElement | null>(null);
  const [openDateTimePicker, setOpenDateTimePicker] = useState<boolean>(false);

  // Menu items inside Manage dropdown
  const manageMenuItems = [
    {
      label: 'Change scheduled time',
      onClick: () => {
        setOpenManageMenu(null);
        setOpenDateTimePicker(true);
      },
    },
    {
      label: 'Publish now',
      onClick: () => {
        setOpenManageMenu(null);
        onPublishNow?.();
      },
    },
  ];

  // Check if the current page is scheduled list page
  const isScheduledList = hashHistory
    ?.getCurrentLocation()
    ?.pathname.includes('/scheduled');

  return (
    <Box
      aria-label={summaryLabel}
      component="article"
      data-testid="scheduled-posts-summary"
      sx={styles.card}
    >
      {!isScheduledList && (
        <React.Fragment key={summaryLabel}>
          <Box component="header" sx={styles.header}>
            <Typography component="h2" sx={styles.headerText}>
              {summaryLabel}
            </Typography>
            <Button
              kind="secondary"
              onClick={onView}
              size="small"
              sx={styles.viewButton}
              type="button"
            >
              {viewLabel}
            </Button>
          </Box>
          <Divider sx={styles.divider} />
        </React.Fragment>
      )}
      <Box sx={styles.metadata}>
        <Box sx={styles.scheduledDetails}>
          <Icons.Schedule aria-hidden="true" sx={styles.scheduleIcon} />
          <Typography component="div" sx={styles.scheduledText}>
            <Box component="span">Scheduled for</Box>
            <Box component="span" sx={styles.scheduledDate}>
              {currentScheduledDate ? (
                <time dateTime={currentScheduledDate}>
                  {formatDate(currentScheduledDate)}
                </time>
              ) : null}
            </Box>
          </Typography>
        </Box>

        <Box sx={styles.metadataActions}>
          <Button
            aria-expanded={Boolean(openManageMenu)}
            aria-haspopup="menu"
            endIcon={<Icons.KeyboardArrowDown aria-hidden="true" />}
            kind="secondary"
            onClick={(event) => setOpenManageMenu(event.currentTarget)}
            size="small"
            sx={styles.changeButton}
            type="button"
          >
            {manageLabel}
          </Button>
          <IconButton
            aria-disabled={!onOpenActions || undefined}
            aria-label={onOpenActions ? actionsLabel : unavailableActionsLabel}
            disabled={actionsDisabled}
            onClick={onOpenActions}
            size="small"
            sx={styles.actionsButton}
            type="button"
          >
            <Icons.MoreVert aria-hidden="true" />
          </IconButton>
        </Box>
      </Box>
      <Menu
        anchorEl={openManageMenu}
        open={Boolean(openManageMenu)}
        onClose={() => setOpenManageMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          '& .MuiPaper-root': {
            minWidth: 180,
          },
        }}
      >
        {manageMenuItems.map((item) => (
          <MenuItem
            key={item.label}
            label={item.label}
            onClick={item.onClick}
          />
        ))}
      </Menu>
      {/* Schedule date time dialog */}
      <SchedulePostDateTimePickerDialog
        isReschedule={true}
        open={openDateTimePicker}
        onClose={() => {
          setOpenDateTimePicker(false);
        }}
        onConfirm={(date: Date) => {
          onChangeScheduledPost(date);
          setOpenDateTimePicker(false);
        }}
        value={new Date(currentScheduledDate || new Date())}
      />
      {attachmentCount === 1 ? (
        <Typography component="p" sx={styles.postPreview}>
          {attachmentSummary}
        </Typography>
      ) : null}
      {attachmentCount > 1 ? (
        <Box
          aria-label={`${attachmentCount} attachments`}
          data-testid="scheduled-post-attachments"
          sx={styles.attachments}
        >
          <Icons.FilePresentOutlined
            aria-hidden="true"
            sx={styles.attachmentIcon}
          />
          <Typography component="span" sx={styles.attachmentText}>
            {attachmentCount > 10 ? '10+' : attachmentCount} attachments
          </Typography>
        </Box>
      ) : null}
      {title ? (
        <Typography component="h3" sx={styles.postTitle}>
          {title}
        </Typography>
      ) : null}
      {preview ? (
        <Typography component="p" sx={styles.postPreview}>
          {preview}
        </Typography>
      ) : null}
      {isScheduledList && renderPostTags}
    </Box>
  );
};

export default ScheduledPostsSummaryView;
